// KnowledgeBank — the Knowledge bank's list (E-01, #60): every source the
// assistant knows and builds material from, as one quiet table. A row says
// what the source is (kind, when added), how far reading it has come (and how
// many facts it holds), and where it is used; one click opens it in its
// viewer.
//
// Only what works today is shown (owner 2026-10-19): the Sources tab. "Facts",
// "About you" and "About your materials" have no workspace-wide read yet and
// are left out, not faked.
//
// A projection + intents: the rows are projected in (knowledgeBankModel);
// opening, adding and a row's lifecycle are intents the host maps to its
// existing commands. Local state is view-ephemeral only: the search text, the
// sort, whether the archive is shown, a rename being typed.

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { formatCount } from "../../app/displayLimits.js";
import { Button } from "../Button/Button.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import { RowOverflow } from "../RowOverflow/RowOverflow.js";
import { RowRename } from "../RowOverflow/RowRename.js";
import { Text } from "../Text/Text.js";
import { TextTabs } from "../TextTabs/TextTabs.js";
import { ViewColumn, ViewHeader } from "../ViewHeader/ViewHeader.js";
import {
    type FileKind,
    type KnowledgeRow,
    type KnowledgeSort,
    shownRows,
    splitForMiddle,
} from "./knowledgeBankModel.js";
import "./KnowledgeBank.css";

// A row's lifecycle action, reported up with the row.
export type KnowledgeRowAction =
    | { readonly type: "rename"; readonly title: string }
    | { readonly type: "archive" }
    | { readonly type: "unarchive" }
    | { readonly type: "delete" };

export type KnowledgeBankProps = {
    readonly rows: readonly KnowledgeRow[];
    // Nothing loaded yet because the server can't be reached.
    readonly waiting: boolean;
    // Files still uploading (no source exists for them yet).
    readonly uploading: readonly string[];
    readonly onOpen: (row: KnowledgeRow) => void;
    readonly onAddSources: () => void;
    readonly onRowAction: (
        row: KnowledgeRow,
        action: KnowledgeRowAction,
    ) => void;
};

// A name cut in the middle when it doesn't fit (the full name in the
// tooltip): the head shrinks with an ellipsis, the tail stays.
const MiddleTruncated = ({
    text,
    className,
}: {
    readonly text: string;
    readonly className?: string;
}): ReactNode => {
    const { head, tail } = splitForMiddle(text);
    return (
        <span
            className={`kb-middle${className ? ` ${className}` : ""}`}
            title={text}
        >
            <span className="kb-middle__head">{head}</span>
            {tail === "" ? null : (
                <span className="kb-middle__tail">{tail}</span>
            )}
        </span>
    );
};

export const fileKindIcon = (kind: FileKind): IconName => {
    switch (kind) {
        case "pdf":
        case "word":
        case "text":
            return "document";
        case "presentation":
            return "presentation";
        case "image":
            return "image";
        case "audio":
            return "audio";
        case "video":
            return "video";
        case "unknown":
            return "unknown-file";
        default:
            return exhaustSwitchCase(kind);
    }
};

// The localised word for a file kind (the meta line, the viewer header).
export const useFileKindLabel = (): ((kind: FileKind) => string) => {
    const { t } = useLingui();
    return (kind) => {
        switch (kind) {
            case "pdf":
                return t`PDF`;
            case "word":
                return t`Word document`;
            case "presentation":
                return t`Presentation`;
            case "text":
                return t`Text`;
            case "image":
                return t`Image`;
            case "audio":
                return t`Audio`;
            case "video":
                return t`Video`;
            case "unknown":
                return t`File`;
            default:
                return exhaustSwitchCase(kind);
        }
    };
};

const SORTS: readonly KnowledgeSort[] = ["added", "title"];

export const KnowledgeBank = ({
    rows,
    waiting,
    uploading,
    onOpen,
    onAddSources,
    onRowAction,
}: KnowledgeBankProps): ReactNode => {
    const { t, i18n } = useLingui();
    const kindLabel = useFileKindLabel();
    // View-ephemeral: the search text, the order, the archive, a rename.
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState<KnowledgeSort>("added");
    const [archived, setArchived] = useState(false);
    const [renamingId, setRenamingId] = useState<string | null>(null);

    const live = rows.filter((r) => !r.archived);
    const archivedCount = rows.length - live.length;
    const shown = shownRows(rows, { query, sort, archived });

    const sortLabel = (s: KnowledgeSort): string => {
        switch (s) {
            case "added":
                return t`Recently added`;
            case "title":
                return t`Title (A–Z)`;
            default:
                return exhaustSwitchCase(s);
        }
    };

    const added = (iso: string | undefined): string | undefined => {
        if (iso === undefined) return undefined;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return undefined;
        const date = d.toLocaleDateString(i18n.locale, {
            day: "numeric",
            month: "short",
        });
        return t`added ${date}`;
    };

    const metaOf = (row: KnowledgeRow): string =>
        [kindLabel(row.fileKind), added(row.createdAt)]
            .filter((part) => part !== undefined)
            .join(" · ");

    const statusOf = (row: KnowledgeRow): ReactNode => {
        if (row.kind === "file")
            return (
                <span className="kb-status">
                    <Icon name="file" size="xs" />
                    {t`Not a source yet`}
                </span>
            );
        const status = row.readStatus;
        if (status?.type === "source_reading_scanned_pages") {
            const pages = status.pageCount;
            return (
                <span className="kb-status kb-status--reading">
                    <span className="kb-status__pulse" aria-hidden="true" />
                    {t`${plural(pages, {
                        one: "Reading # scanned page…",
                        other: "Reading # scanned pages…",
                    })}`}
                </span>
            );
        }
        if (status?.type === "source_read_failed")
            return (
                <span className="kb-status kb-status--failed">
                    <Icon name="warning" size="xs" />
                    {t`Couldn't be read`}
                </span>
            );
        const facts = row.facts;
        return (
            <span className="kb-status kb-status--read">
                <Icon name="check" size="xs" />
                {facts === 0
                    ? t`Read`
                    : t`${plural(facts, {
                          one: "Read · # fact",
                          other: "Read · # facts",
                      })}`}
            </span>
        );
    };

    const usedOf = (row: KnowledgeRow): string =>
        row.usedIn === 0
            ? "–"
            : t`${plural(row.usedIn, {
                  one: "In # material",
                  other: "In # materials",
              })}`;

    const menuOf = (row: KnowledgeRow): readonly MenuItem[] => [
        {
            _tag: "item",
            label: t`Rename`,
            onSelect: () => setRenamingId(row.id),
        },
        row.archived
            ? {
                  _tag: "item",
                  label: t`Unarchive`,
                  onSelect: () => onRowAction(row, { type: "unarchive" }),
              }
            : {
                  _tag: "item",
                  label: t`Archive`,
                  onSelect: () => onRowAction(row, { type: "archive" }),
              },
        {
            _tag: "item",
            label: t`Delete…`,
            tone: "danger",
            onSelect: () => onRowAction(row, { type: "delete" }),
        },
    ];

    const sourcesCount = formatCount(live.length, i18n.locale);

    const body = (): ReactNode => {
        if (waiting)
            return (
                <EmptyState
                    icon={<Icon name="wifi-off" size="lg" />}
                    title={t`Can't show the knowledge bank right now`}
                    body={t`It appears as soon as the connection to the server is back.`}
                />
            );
        if (shown.length === 0)
            return (
                <EmptyState
                    icon={<Icon name="knowledge" size="lg" />}
                    title={
                        query.trim() !== ""
                            ? t`No sources match "${query}"`
                            : archived
                              ? t`Nothing archived`
                              : t`No sources yet`
                    }
                    body={
                        query.trim() !== "" || archived
                            ? undefined
                            : t`Add the texts, files and media you teach from. The assistant reads them and builds material only on what is here.`
                    }
                >
                    {query.trim() === "" && !archived ? (
                        <Button variant="primary" onClick={onAddSources}>
                            {t`Add sources`}
                        </Button>
                    ) : null}
                </EmptyState>
            );
        return (
            <ul className="kb-list" aria-label={t`Sources`}>
                {shown.map((row) => (
                    <li key={row.id} className="kb-row">
                        {row.id === renamingId ? (
                            <div className="kb-row__rename">
                                <RowRename
                                    initial={row.title}
                                    label={t`New name for ${row.title}`}
                                    onSubmit={(next) => {
                                        setRenamingId(null);
                                        onRowAction(row, {
                                            type: "rename",
                                            title: next,
                                        });
                                    }}
                                    onCancel={() => setRenamingId(null)}
                                />
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="kb-row__open"
                                onClick={() => onOpen(row)}
                            >
                                <Icon
                                    name={fileKindIcon(row.fileKind)}
                                    size="sm"
                                    className="kb-row__icon"
                                />
                                <span className="kb-row__name">
                                    <MiddleTruncated
                                        text={row.title}
                                        className="kb-row__title"
                                    />
                                    <span className="kb-row__meta">
                                        {metaOf(row)}
                                    </span>
                                </span>
                                <span className="kb-row__status">
                                    {statusOf(row)}
                                </span>
                                <span className="kb-row__used">
                                    {usedOf(row)}
                                </span>
                            </button>
                        )}
                        <RowOverflow
                            label={t`Actions for ${row.title}`}
                            items={menuOf(row)}
                            className="kb-row__menu"
                        />
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <ViewColumn className="kb">
            <ViewHeader
                title={t`Knowledge bank`}
                actions={
                    <Button
                        variant="primary"
                        className="kb-add"
                        onClick={onAddSources}
                    >
                        {t`Add sources`}
                    </Button>
                }
                tools={
                    <>
                        <Text as="p" size="md" tone="dim" className="kb-lede">
                            {t`Everything the assistant knows and can build material on. What isn't here, it doesn't use.`}
                        </Text>
                        <label className="kb-search">
                            <Icon name="search" size="sm" />
                            <input
                                type="search"
                                className="kb-search__input"
                                placeholder={t`Search the sources`}
                                aria-label={t`Search the sources`}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </label>
                    </>
                }
                nav={
                    waiting ? undefined : (
                        <div className="kb-nav">
                            <TextTabs
                                ariaLabel={t`Knowledge bank`}
                                tabs={[
                                    {
                                        value: "sources",
                                        label: t`Sources  ${sourcesCount}`,
                                    },
                                ]}
                                value="sources"
                                onChange={() => setArchived(false)}
                            />
                            <div className="kb-nav__tools">
                                {archivedCount > 0 || archived ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        aria-pressed={archived}
                                        onClick={() => setArchived((a) => !a)}
                                    >
                                        <Icon name="archive" size="sm" />
                                        {archived
                                            ? t`Back to the sources`
                                            : t`Archived (${formatCount(archivedCount, i18n.locale)})`}
                                    </Button>
                                ) : null}
                                <Menu
                                    align="end"
                                    trigger={
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            className="kb-sort"
                                        >
                                            {sortLabel(sort)}
                                            <Icon
                                                name="chevron-down"
                                                size="sm"
                                            />
                                        </Button>
                                    }
                                    items={SORTS.map(
                                        (s): MenuItem => ({
                                            _tag: "item",
                                            label: sortLabel(s),
                                            icon:
                                                s === sort ? (
                                                    <Icon
                                                        name="check"
                                                        size="xs"
                                                    />
                                                ) : undefined,
                                            onSelect: () => setSort(s),
                                        }),
                                    )}
                                />
                            </div>
                        </div>
                    )
                }
            />
            <div role="status" className="kb-uploading">
                {uploading.map((filename, i) => (
                    <Text
                        // biome-ignore lint/suspicious/noArrayIndexKey: two queued files may share a name; the batch order is stable
                        key={i}
                        as="p"
                        size="xs"
                        tone="dim"
                    >
                        {t`Uploading ${filename}…`}
                    </Text>
                ))}
            </div>
            {body()}
        </ViewColumn>
    );
};
